// ============================================================
// 📄 src/store/authStore.ts - ИСПРАВЛЕННАЯ ВЕРСИЯ
// ============================================================
// 🔧 ИСПРАВЛЕНО:
// 1. Улучшена обработка ошибок от API с детальными сообщениями
// 2. Добавлено логирование всех этапов авторизации
// 3. Улучшен fallback на DEV режим
// 4. Добавлена валидация токена перед сохранением/восстановлением
// 5. ✅ ВОЗВРАТИЛИ ПРОКИДЫВАНИЕ TOKEN: синхронизация с apiClient и localStorage('auth_token')
// 6. ✅ DEV mock: инициализация mock-продуктов
// 7. 🧹 Чёткая очистка просроченных/битых сессий (включая QUESTIONNAIRE_DONE)
// ============================================================

import { create } from 'zustand';
import { useRetinolStore } from './retinolStore';
import { apiClient, checkApiHealth, ApiError } from '../services/api';
import { syncTimezoneToCloud } from '../utils/cloudStorage';

const DEV_MODE = process.env.REACT_APP_DEV_MODE === 'true';

interface User {
  id: number;
  telegram_id: number;
  real_name?: string | null; // ✅ БАГ 3 FIX: бекенд отдает real_name
  first_name: string | null;  // Legacy (fallback)
  last_name?: string | null;
  username?: string | null;
  language_code?: string;
  is_premium?: boolean;
  photo_url?: string | null;
  timezone?: string;
  created_at?: string;
  updated_at?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  user: User | null;
  questionnaireCompleted: boolean;
  apiStatus: 'unknown' | 'ok' | 'error';

  // Actions
  initializeTelegramAuth: (initData: string, telegramUser?: any) => Promise<void>;
  logout: () => void;
  checkAuth: () => void;
  clearError: () => void;
  setQuestionnaireCompleted: (completed: boolean) => void;
  updateTimezone: (timezone: string) => Promise<void>; // ✅ async для API + CloudStorage
  updateUser: (userData: Partial<User>) => void; // ✅ Обновление данных пользователя
  checkApiStatus: () => Promise<void>;
  refetchUserData: () => Promise<{ questionnaireChanged: boolean; newValue: boolean | null }>; // ✅ Рефетч данных пользователя с API
}

const STORAGE_KEYS = {
  USER: 'geltek_user',
  AUTH_METHOD: 'geltek_auth_method',
  LAST_AUTH: 'geltek_last_auth',
  QUESTIONNAIRE_DONE: 'geltek_questionnaire_done',
  // токен берём/кладём в 'auth_token' — это ключ, который уже использует api.ts
} as const;

const parseInitData = (initData: string): any => {
  try {
    const urlParams = new URLSearchParams(initData);
    const userParam = urlParams.get('user');

    if (userParam) {
      return JSON.parse(decodeURIComponent(userParam));
    }
    return null;
  } catch (error) {
    console.warn('⚠️ Ошибка парсинга initData:', error);
    return null;
  }
};

const createMockUser = (telegramUser?: any): User => {
  const baseUser =
    telegramUser || {
      id: parseInt(process.env.REACT_APP_MOCK_USER_ID || '399755386', 10),
      first_name: null,
      last_name: null,
      username: null,
      language_code: 'ru',
      is_premium: false,
      timezone: undefined as string | undefined,
    };

  return {
    id: baseUser.id,
    telegram_id: baseUser.id,
    first_name: baseUser.first_name,
    last_name: baseUser.last_name ?? undefined,
    username: baseUser.username ?? undefined,
    language_code: baseUser.language_code ?? undefined,
    is_premium: baseUser.is_premium ?? undefined,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    timezone: baseUser.timezone,
  };
};

export const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: false,
  isLoading: false,
  error: null,
  user: null,
  questionnaireCompleted: false,
  apiStatus: 'unknown',

  checkApiStatus: async () => {
    try {
      const health = await checkApiHealth();
      set({ apiStatus: health.status });
      if (health.status === 'error') {
        console.error('❌ API недоступен:', health.message);
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ API доступен');
        }
      }
    } catch (error) {
      set({ apiStatus: 'error' });
      console.error('❌ Ошибка проверки API:', error);
    }
  },

  initializeTelegramAuth: async (initData: string, telegramUser?: any) => {
    const currentState = get();
    if (currentState.isLoading) {
      if (process.env.NODE_ENV === 'development') {
        console.log('⏳ Авторизация уже в процессе, пропускаем...');
      }
      return;
    }

    set({ isLoading: true, error: null });

    // Проверяем доступность API
    await get().checkApiStatus();

    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 Начинаем авторизацию через API...');
        console.log('📊 initData длина:', initData?.length || 0);
        console.log('📊 Первые 20 символов initData:', initData?.substring(0, 20) || 'нет данных');
      }

      // ✅ Вызываем authTelegram: api.ts сам сохранит токен в this.token и localStorage('auth_token')
      const response = await apiClient.authTelegram(initData);

      if (process.env.NODE_ENV === 'development') {
        console.log('📥 Получен ответ от API:', {
          success: response.success,
          hasToken: !!response.token,
          hasUser: !!response.user,
          questionaryDone: response.user?.questionary_done,
        });
      }

      // ✅ Проверяем валидность ответа
      if (!response.success) {
        throw new ApiError({
          status: 200,
          message: 'API вернул success: false',
        });
      }

      // ✅ FIX: Безопасная проверка токена с проверкой типа перед .trim()
      if (!response.token || typeof response.token !== 'string' || response.token.trim().length < 10) {
        throw new ApiError({
          status: 200,
          message: 'API не вернул валидный токен',
        });
      }

      if (!response.user || !response.user.id_telegram) {
        throw new ApiError({
          status: 200,
          message: 'API не вернул данные пользователя',
        });
      }

      // ✅ Создаём объект пользователя с fallback на telegramUser если бэк вернул null
      // Это важно после /reset когда бэк обнуляет данные пользователя
      const user: User = {
        id: response.user.id_telegram,
        telegram_id: response.user.id_telegram,
        // ✅ ИСПРАВЛЕНО: Используем данные из telegramUser если бэкенд вернул null
        first_name: response.user.first_name || telegramUser?.first_name || 'Гость',
        last_name: response.user.last_name ?? telegramUser?.last_name ?? undefined,
        username: response.user.username ?? telegramUser?.username ?? undefined,
        language_code: response.user.language_code ?? telegramUser?.language_code ?? undefined,
        is_premium: response.user.is_premium ?? telegramUser?.is_premium ?? undefined,
        photo_url: response.user.photo_url ?? telegramUser?.photo_url ?? undefined,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        timezone: undefined,
      };

      const questionnaireCompleted = response.user.questionary_done || false;

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Пользователь создан:', {
          id: user.id,
          questionnaireCompleted,
        });
      }

      // Если анкета пройдена — инициализируем календарь и stage
      if (questionnaireCompleted && response.dates) {
        const retinolStore = useRetinolStore.getState();
        retinolStore.initializeFromBackend(
          response.dates,
          response.recommend_products ||[],
          response.user.plan_start_use_retinol || null
        );

        if (response.user.stage) {
          retinolStore.setStageFromBackend(response.user.stage);
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ Stage установлен из API:', response.user.stage);
          }
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Календарь загружен:', response.dates.length, 'дат');
        }
      }

      // Сохраняем в localStorage
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
      localStorage.setItem(STORAGE_KEYS.AUTH_METHOD, 'telegram_api');
      localStorage.setItem(STORAGE_KEYS.LAST_AUTH, Date.now().toString());
      localStorage.setItem(
        STORAGE_KEYS.QUESTIONNAIRE_DONE,
        questionnaireCompleted.toString()
      );

      // ✅ Дублируем установку токена в клиент и localStorage на случай условий гонки/импорта
      if (response.token) {
        apiClient.setToken(response.token.trim());
        localStorage.setItem('auth_token', response.token.trim());
      }

      set({
        isAuthenticated: true,
        isLoading: false,
        user,
        error: null,
        questionnaireCompleted,
        apiStatus: 'ok',
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Авторизация успешна через API');
      }
    } catch (error: any) {
      console.error('❌ Ошибка авторизации через API:', error);

      // 🧹 В PROD не держим «грязный» токен после неудачной авторизации
      if (!DEV_MODE) {
        try {
          localStorage.removeItem('auth_token');
        } catch {}
        apiClient.clearToken();
      }

      // ✅ Более детальное сообщение об ошибке
      let uiMessage = 'Не удалось авторизоваться';

      if (error instanceof ApiError) {
        uiMessage = error.message || `Ошибка ${error.status}`;

        if (error.status === 500) {
          uiMessage = 'Ошибка сервера. Попробуйте позже';
        } else if (error.status === 401) {
          uiMessage = 'Данные Telegram недействительны';
        } else if (error.status === 422) {
          uiMessage = 'Некорректные данные авторизации';
        } else if (error.status === 0) {
          uiMessage = 'Нет связи с сервером';
        }
      } else if (error?.message) {
        uiMessage = error.message;
      }

      console.error('📝 Сообщение для пользователя:', uiMessage);

      // ✅ DEV MODE: Используем mock авторизацию
      if (DEV_MODE) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 DEV MODE: Используем mock авторизацию');
        }

        let user: User;
        let authMethod: string;

        if (telegramUser) {
          user = createMockUser(telegramUser);
          authMethod = 'mock_telegram_webapp';
        } else if (initData && initData.length > 10) {
          const parsedUser = parseInitData(initData);
          user = createMockUser(parsedUser || undefined);
          authMethod = 'mock_telegram_initdata';
        } else {
          user = createMockUser();
          authMethod = 'mock_dev_fallback';
        }

        const questionnaireCompleted =
          localStorage.getItem(STORAGE_KEYS.QUESTIONNAIRE_DONE) === 'true';

        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
        localStorage.setItem(STORAGE_KEYS.AUTH_METHOD, authMethod);
        localStorage.setItem(STORAGE_KEYS.LAST_AUTH, Date.now().toString());

        // ✅ Инициализация продуктов в mock-режиме
        const retinolStore = useRetinolStore.getState();

        const mockProducts = DEV_MODE
          ?[
              {
                product_id: 'mock_1',
                product_name: 'Ретинол 0.2% для начинающих',
                product_url: 'https://geltek.ru/product/retinol-02',
                product_photo: 'https://geltek.ru/images/retinol-02.jpg',
                description:
                  'Идеально для первого знакомства с ретинолом. Мягкая формула для чувствительной кожи.',
              },
            ]
          :[];

        retinolStore.initializeFromBackend([], mockProducts);

        // На всякий случай чистим auth_token и токен в клиенте, чтобы не дергать защищённые ручки
        localStorage.removeItem('auth_token');
        apiClient.clearToken();

        set({
          isAuthenticated: true,
          isLoading: false,
          user,
          error: null,
          questionnaireCompleted,
          apiStatus: 'error',
        });

        if (process.env.NODE_ENV === 'development') {
          console.log(`✅ Mock авторизация (${authMethod})`);
        }
      } else {
        // ✅ PROD MODE: Показываем ошибку пользователю
        set({
          isAuthenticated: false,
          isLoading: false,
          error: uiMessage,
          apiStatus: 'error',
        });

        console.error('❌ Авторизация не удалась (PROD режим)');
      }
    }
  },

  checkAuth: () => {
    try {
      const savedUser = localStorage.getItem(STORAGE_KEYS.USER);
      const authMethod = localStorage.getItem(STORAGE_KEYS.AUTH_METHOD);
      const lastAuth = localStorage.getItem(STORAGE_KEYS.LAST_AUTH);
      const questionnaireCompleted =
        localStorage.getItem(STORAGE_KEYS.QUESTIONNAIRE_DONE) === 'true';

      const hasStoredAuth = Boolean(savedUser && authMethod && lastAuth);
      const tokenRaw = localStorage.getItem('auth_token') || '';
      const token = tokenRaw.trim();
      const hasToken = Boolean(token);

      if (hasStoredAuth) {
        const lastAuthNum = parseInt(lastAuth as string, 10);
        const authAge = isNaN(lastAuthNum) ? Number.POSITIVE_INFINITY : Date.now() - lastAuthNum;
        const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 дней

        if (authAge < maxAge && hasToken) {
          let user: User | null = null;
          try {
            user = JSON.parse(savedUser as string) as User;
          } catch {
            user = null;
          }

          if (user) {
            // ✅ FIX: Проверка expiry JWT токена
            try {
              const parts = token.split('.');
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]));
                if (payload.exp && payload.exp * 1000 < Date.now()) {
                  // Токен истек
                  console.warn('⚠️ JWT токен истек, требуется повторная авторизация');
                  get().logout();
                  return;
                }
              }
            } catch (jwtError) {
              console.warn('⚠️ Не удалось проверить expiry JWT:', jwtError);
              // Продолжаем, т.к. возможно токен не JWT или другой формат
            }

            apiClient.setToken(token);

            set({
              isAuthenticated: true,
              user,
              error: null,
              questionnaireCompleted,
            });

            if (process.env.NODE_ENV === 'development') {
              console.log(`✅ Восстановлена авторизация (${authMethod})`);
            }
            return;
          }
        }

        if (!hasToken) {
          console.warn('⚠️ Найдены данные пользователя без токена — очищаем сторадж');
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.log('⏰ Авторизация устарела или данные повреждены');
          }
        }

        // 🧹 Полная очистка просроченной/битой сессии
        localStorage.removeItem(STORAGE_KEYS.USER);
        localStorage.removeItem(STORAGE_KEYS.AUTH_METHOD);
        localStorage.removeItem(STORAGE_KEYS.LAST_AUTH);
        localStorage.removeItem(STORAGE_KEYS.QUESTIONNAIRE_DONE);
      }

      // 🧹 Токен отдельно
      if (!hasToken) localStorage.removeItem('auth_token');
      apiClient.clearToken();

      set({
        isAuthenticated: false,
        user: null,
        error: null,
        questionnaireCompleted: false,
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('ℹ️ Пользователь не авторизован');
      }
    } catch (error) {
      console.error('❌ Ошибка проверки авторизации:', error);

      localStorage.removeItem(STORAGE_KEYS.USER);
      localStorage.removeItem(STORAGE_KEYS.AUTH_METHOD);
      localStorage.removeItem(STORAGE_KEYS.LAST_AUTH);
      localStorage.removeItem(STORAGE_KEYS.QUESTIONNAIRE_DONE);
      localStorage.removeItem('auth_token');
      apiClient.clearToken();

      set({
        isAuthenticated: false,
        user: null,
        error: null,
        questionnaireCompleted: false,
      });
    }
  },

  setQuestionnaireCompleted: (completed: boolean) => {
    localStorage.setItem(STORAGE_KEYS.QUESTIONNAIRE_DONE, completed.toString());
    set({ questionnaireCompleted: completed });
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ Статус анкеты установлен: ${completed}`);
    }
  },

  updateTimezone: async (timezone: string) => {
    const currentUser = get().user;
    if (!currentUser) return;

    const updatedUser: User = {
      ...currentUser,
      timezone,
    };

    // 1. Обновляем state и localStorage
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));
    set({ user: updatedUser });

    // 2. Отправляем на бекенд
    try {
      await apiClient.updateTimezone({ timezone });
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Timezone отправлен на бекенд:', timezone);
      }
    } catch (error) {
      console.error('❌ Ошибка отправки timezone на бекенд:', error);
    }

    // 3. Синхронизируем в CloudStorage для кросс-девайс синхронизации
    try {
      await syncTimezoneToCloud(timezone);
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Timezone синхронизирован между устройствами');
      }
    } catch (error) {
      console.error('❌ Ошибка синхронизации timezone в CloudStorage:', error);
    }
  },

  logout: () => {
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.AUTH_METHOD);
    localStorage.removeItem(STORAGE_KEYS.LAST_AUTH);
    localStorage.removeItem(STORAGE_KEYS.QUESTIONNAIRE_DONE);

    // ✅ удаляем и токен, и чистим клиент
    localStorage.removeItem('auth_token');
    apiClient.clearToken();

    set({
      isAuthenticated: false,
      user: null,
      error: null,
      questionnaireCompleted: false,
      apiStatus: 'unknown',
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Выход выполнен');
    }
  },

  // ✅ Обновление данных пользователя (real_name, photo_url и т.д.)
  updateUser: (userData: Partial<User>) => {
    set((state) => {
      if (!state.user) {
        console.warn('⚠️ Попытка обновить user, но user === null');
        return state;
      }

      const updatedUser = { ...state.user, ...userData };

      // Сохраняем в localStorage для persist
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Данные пользователя обновлены:', userData);
      }
      return { user: updatedUser };
    });
  },

  clearError: () => {
    set({ error: null });
  },

  // ✅ Рефетч данных пользователя с API для синхронизации между устройствами
  refetchUserData: async () => {
    const currentState = get();

    // Если нет токена или не авторизован, не делаем запрос
    const token = localStorage.getItem('auth_token');
    if (!token || !currentState.isAuthenticated) {
      if (process.env.NODE_ENV === 'development') {
        console.log('ℹ️ Пропуск refetch: нет токена или не авторизован');
      }
      return { questionnaireChanged: false, newValue: null };
    }

    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 Рефетч данных пользователя...');
      }

      const userInfo = await apiClient.getUserInfo();

      const oldValue = currentState.questionnaireCompleted;
      const newValue = userInfo.questionary_done || false;
      const questionnaireChanged = oldValue !== newValue;

      if (questionnaireChanged && process.env.NODE_ENV === 'development') {
        console.log(`⚠️ questionary_done изменился: ${oldValue} → ${newValue}`);
      }

      // Обновляем статус анкеты
      if (questionnaireChanged) {
        localStorage.setItem(STORAGE_KEYS.QUESTIONNAIRE_DONE, newValue.toString());
        set({ questionnaireCompleted: newValue });
      }

      // ✅ FIX: Обновляем данные календаря ИЛИ очищаем если анкета сброшена
      if (newValue && userInfo.dates) {
        const retinolStore = useRetinolStore.getState();
        retinolStore.initializeFromBackend(
          userInfo.dates,
          userInfo.recommend_products || []
        );
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Календарь обновлён из API');
        }
      } else if (!newValue) {
        // Анкета сброшена на другом устройстве - очищаем календарь
        const retinolStore = useRetinolStore.getState();
        retinolStore.initializeFromBackend([], []);
        if (process.env.NODE_ENV === 'development') {
          console.log('🧹 Календарь очищен (анкета сброшена на другом устройстве)');
        }
      }

      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ Refetch успешен. questionary_done = ${newValue}`);
      }
      return { questionnaireChanged, newValue };
    } catch (error: any) {
      console.error('❌ Ошибка refetch данных пользователя:', error);

      // При 401 (Unauthorized) - разлогиниваем
      if (error?.status === 401) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔐 Получен 401, выполняем logout');
        }
        get().logout();
      }

      return { questionnaireChanged: false, newValue: null };
    }
  },
}));
